import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCookieConsent } from '@/contexts/CookieConsentContext';

export default function CookieBanner() {
  const {
    bannerVisivel,
    preferenciasAbertas,
    abrirPreferencias,
    fecharPreferencias,
    aceitarTodos,
    rejeitarNaoEssenciais,
    salvarPreferencias,
    consent,
  } = useCookieConsent();

  const [analiticos, setAnaliticos] = useState(consent?.analiticos ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);

  useEffect(() => {
    if (preferenciasAbertas) {
      setAnaliticos(consent?.analiticos ?? false);
      setMarketing(consent?.marketing ?? false);
    }
  }, [preferenciasAbertas, consent]);

  return (
    <>
      {bannerVisivel && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de consentimento de cookies"
          className="fixed inset-x-0 bottom-0 z-50 border-t bg-card/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                Utilizamos cookies para melhorar sua experiência. Ao continuar navegando, você
                concorda com nossa política de cookies.{' '}
                <Link
                  to="/privacidade"
                  className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
                >
                  Política de Privacidade e Cookies
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
              <Button variant="ghost" size="sm" onClick={abrirPreferencias}>
                Gerenciar preferências
              </Button>
              <Button variant="outline" size="sm" onClick={rejeitarNaoEssenciais}>
                Rejeitar não essenciais
              </Button>
              <Button size="sm" onClick={aceitarTodos}>
                Aceitar todos
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={preferenciasAbertas} onOpenChange={(o) => (o ? abrirPreferencias() : fecharPreferencias())}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Preferências de cookies</DialogTitle>
            <DialogDescription>
              Escolha quais categorias deseja permitir. Você pode alterar essa decisão a qualquer
              momento pelo rodapé do sistema.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1">
                <Label htmlFor="cookie-essenciais" className="text-sm font-medium">
                  Essenciais
                </Label>
                <p className="text-xs text-muted-foreground">
                  Necessários para autenticação, segurança e funcionamento básico. Sempre ativos.
                </p>
              </div>
              <Switch id="cookie-essenciais" checked disabled aria-label="Cookies essenciais sempre ativos" />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1">
                <Label htmlFor="cookie-analiticos" className="text-sm font-medium">
                  Analíticos
                </Label>
                <p className="text-xs text-muted-foreground">
                  Ajudam a entender o uso do sistema para melhorar a experiência. Opcional.
                </p>
              </div>
              <Switch id="cookie-analiticos" checked={analiticos} onCheckedChange={setAnaliticos} />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1">
                <Label htmlFor="cookie-marketing" className="text-sm font-medium">
                  Marketing
                </Label>
                <p className="text-xs text-muted-foreground">
                  Utilizados para comunicações e conteúdos personalizados. Opcional.
                </p>
              </div>
              <Switch id="cookie-marketing" checked={marketing} onCheckedChange={setMarketing} />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => salvarPreferencias({ analiticos, marketing })}>
              Salvar preferências
            </Button>
            <Button onClick={aceitarTodos}>Aceitar todos</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
